import { useMutation, useQueryClient } from "@tanstack/react-query";
import type { AxiosInstance } from "axios";
import type {
  ApiResponse,
  PtAppointmentDTO,
  PtCreateAppointmentRequest,
  PtTrainerCreateAppointmentRequest,
} from "@membentry/shared-types";
import * as api from "@membentry/shared-api";

import {
  invalidatePtMember,
  invalidatePtTrainer,
  invalidatePtTrainerAndMember,
} from "./invalidation";
import type {
  AppointmentVariables,
  ClubVariables,
  MutationOptions,
} from "./types";
import { usePtMutation } from "./use-pt-mutation";

export const useCreatePtAppointmentMutation = (
  client: AxiosInstance,
  options?: MutationOptions<
    PtAppointmentDTO,
    PtCreateAppointmentRequest & ClubVariables
  >,
) => {
  const queryClient = useQueryClient();
  return useMutation({
    ...options,
    mutationFn: ({ clubId, ...body }) =>
      api.createPtAppointment(client, clubId, body),
    onSuccess: async (data, variables, onMutateResult, context) => {
      invalidatePtMember(queryClient, variables.clubId);
      await options?.onSuccess?.(data, variables, onMutateResult, context);
    },
  });
};

export const useCancelPtAppointmentMutation = (
  client: AxiosInstance,
  options?: MutationOptions<PtAppointmentDTO, AppointmentVariables>,
) => {
  const queryClient = useQueryClient();
  return useMutation({
    ...options,
    mutationFn: (variables) =>
      api.cancelPtAppointment(client, variables.clubId, variables.id),
    onSuccess: async (data, variables, onMutateResult, context) => {
      invalidatePtMember(queryClient, variables.clubId);
      invalidatePtTrainer(queryClient, variables.clubId);
      await options?.onSuccess?.(data, variables, onMutateResult, context);
    },
  });
};

export const useCreatePtAppointmentForMemberMutation = (
  client: AxiosInstance,
  options?: MutationOptions<
    PtAppointmentDTO,
    PtTrainerCreateAppointmentRequest & ClubVariables
  >,
) =>
  usePtMutation(
    client,
    options,
    (apiClient, variables) => {
      const { clubId, memberId, startAt, note, confirmOverlap } = variables;
      return api.createPtAppointmentForMember(apiClient, clubId, {
        memberId,
        startAt,
        ...(note === undefined ? {} : { note }),
        ...(confirmOverlap === undefined ? {} : { confirmOverlap }),
      });
    },
    invalidatePtTrainerAndMember,
  );

const appointmentAction = (
  action: (
    client: AxiosInstance,
    clubId: number,
    id: number,
  ) => Promise<ApiResponse<PtAppointmentDTO>>,
) =>
  (client: AxiosInstance, variables: AppointmentVariables) =>
    action(client, variables.clubId, variables.id);

export const useApprovePtAppointmentMutation = (
  client: AxiosInstance,
  options?: MutationOptions<PtAppointmentDTO, AppointmentVariables>,
) =>
  usePtMutation(
    client,
    options,
    appointmentAction(api.approvePtAppointment),
    invalidatePtTrainer,
  );

export const useDeclinePtAppointmentMutation = (
  client: AxiosInstance,
  options?: MutationOptions<PtAppointmentDTO, AppointmentVariables>,
) =>
  usePtMutation(
    client,
    options,
    appointmentAction(api.declinePtAppointment),
    invalidatePtTrainer,
  );

export const useCompletePtAppointmentMutation = (
  client: AxiosInstance,
  options?: MutationOptions<PtAppointmentDTO, AppointmentVariables>,
) =>
  usePtMutation(
    client,
    options,
    appointmentAction(api.completePtAppointment),
    invalidatePtTrainer,
  );

export const useMarkPtNoShowMutation = (
  client: AxiosInstance,
  options?: MutationOptions<PtAppointmentDTO, AppointmentVariables>,
) =>
  usePtMutation(
    client,
    options,
    appointmentAction(api.markPtNoShow),
    invalidatePtTrainer,
  );
